import React, { useEffect, useState } from "react";
import { Typography, Box, Paper, CircularProgress, Grid, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { MDBContainer } from "mdb-react-ui-kit";
import { useNavigate } from "react-router-dom";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import { makeThemedOptions } from "../utils/chartjs";
import AdminService from "../api/AdminService";
import dayjs from "dayjs";

const DashboardPage = () => {
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector((state) => state.user.isLoading);
  const subscriptions = useSelector((state) => state.user.subscriptions);
  const navigate = useNavigate();
  const theme = useTheme();

  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [storageUsageMap, setStorageUsageMap] = useState({});
  const [usageError, setUsageError] = useState(null);

  useEffect(() => {
    if (!isLoading && !isLoggedIn) {
      navigate("/login");
    }
    if (!isLoading && isLoggedIn) {
      fetchUsers();
    }
  }, [isLoading, isLoggedIn]);

  const fetchUsers = async () => {
    try {
      setLoadingData(true);
      const list = await AdminService.getAllUsers();
      const usersList = Array.isArray(list) ? list : [];
      // Consider only accounts that have a non-empty email
      const usersWithEmail = usersList.filter((u) => typeof u?.email === "string" && u.email.trim().length > 0);
      setUsers(usersWithEmail);
      setError(null);
      // Fetch usage for filtered users only
      try {
        const usageData = await Promise.all(
          usersWithEmail.map(async (u) => {
            try {
              const used = await AdminService.getUserStorageUsage(u.id);
              return { userId: u.id, used };
            } catch (e) {
              return { userId: u.id, used: 0 };
            }
          })
        );
        const map = usageData.reduce((acc, { userId, used }) => {
          acc[userId] = typeof used === "number" ? used : Number(used) || 0;
          return acc;
        }, {});
        setStorageUsageMap(map);
        setUsageError(null);
      } catch (e) {
        setUsageError(e?.message || "Не вдалося завантажити використане місце");
      }
    } catch (e) {
      setError(e?.message || "Не вдалося завантажити користувачів");
    } finally {
      setLoadingData(false);
    }
  };

  if (!isLoading && !isLoggedIn) {
    return <div>Loading...</div>;
  }

  // Build monthly cumulative series from user.created
  const validDates = users
    .map((u) => u?.created)
    .filter(Boolean)
    .map((d) => dayjs(d))
    .filter((d) => d.isValid())
    .map((d) => d.startOf("month"));

  let labels;
  let dataPoints;

  if (validDates.length > 0) {
    const start = validDates.reduce((min, d) => (d.isBefore(min) ? d : min), validDates[0]);
    const end = dayjs().startOf("month");

    // Count registrations per month
    const counts = new Map();
    validDates.forEach((d) => {
      const key = d.format("YYYY-MM");
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    // Generate all months between start..end and make cumulative sum
    const months = [];
    let cursor = start.clone();
    while (cursor.isBefore(end) || cursor.isSame(end)) {
      months.push(cursor.format("YYYY-MM"));
      cursor = cursor.add(1, "month");
    }

    labels = months;
    let running = 0;
    dataPoints = months.map((m) => {
      running += counts.get(m) || 0;
      return running;
    });
  } else {
    // Fallback when no created dates: single point with total users
    labels = ["Всього користувачів"];
    dataPoints = [users.length || 0];
  }

  const primary = theme.palette.primary.main;
  const lineDataset = {
    labels,
    datasets: [
      {
        label: "Кількість користувачів (накопичено)",
        data: dataPoints,
        borderColor: primary,
        backgroundColor: alpha(primary, 0.25),
        fill: true,
        tension: 0.3,
        pointRadius: 2.5,
      },
    ],
  };

  const lineOptions = makeThemedOptions(theme, {
    plugins: {
      legend: { display: true, position: "top" },
    },
    scales: {
      y: { beginAtZero: true },
    },
  });

  // Subscription distribution (percent per subscription)
  const subMap = new Map();
  users.forEach((u) => {
    const key = u.subscriptionId == null ? "none" : String(u.subscriptionId);
    subMap.set(key, (subMap.get(key) || 0) + 1);
  });

  // Build labels and counts sorted by count desc
  const subEntries = Array.from(subMap.entries());
  subEntries.sort((a, b) => b[1] - a[1]);

  const subTotal = subEntries.reduce((sum, [, c]) => sum + c, 0);

  const subLabelsRaw = subEntries.map(([key]) => {
    if (key === "none") return "Без підписки";
    const sub = subscriptions.find((s) => String(s.id) === key);
    return sub?.title || `Підписка ${key}`;
  });
  const subCounts = subEntries.map(([, c]) => c);
  const subPercents = subCounts.map((c) => (subTotal > 0 ? (c / subTotal) * 100 : 0));
  const subLabels = subLabelsRaw.map((l, i) => `${l} (${subPercents[i].toFixed(1)}%)`);

  // Colors for slices
  function generateColors(n) {
    const base = [
      theme.palette.primary.main,
      theme.palette.success?.main || "#4caf50",
      theme.palette.warning?.main || "#ff9800",
      theme.palette.error?.main || "#f44336",
      theme.palette.info?.main || "#03a9f4",
      "#9c27b0",
      "#8bc34a",
      "#ff5722",
      "#607d8b",
    ];
    if (n <= base.length) return base.slice(0, n).map((c) => alpha(c, 0.8));
    // Generate more via HSL
    const out = base.map((c) => alpha(c, 0.8));
    for (let i = out.length; i < n; i++) {
      const hue = Math.round((i * 360) / n);
      out.push(`hsl(${hue} 70% 50%)`);
    }
    return out;
  }

  const doughnutData = {
    labels: subLabels,
    datasets: [
      {
        label: "Користувачі",
        data: subCounts,
        backgroundColor: generateColors(subCounts.length),
        borderWidth: 1,
      },
    ],
  };

  const doughnutOptions = makeThemedOptions(theme, {
    plugins: {
      legend: { position: "right" },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = subLabelsRaw[ctx.dataIndex] || "";
            const value = ctx.parsed || 0;
            const total = subTotal || 0;
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
            return `${label}: ${value} (${pct}%)`;
          },
        },
      },
    },
  });
  delete doughnutOptions.scales;

  // Top 5 users by used storage (MB)
  const topUsers = [...users]
    .map((u) => ({ email: u.email, used: storageUsageMap[u.id] ?? 0 }))
    .sort((a, b) => b.used - a.used)
    .slice(0, 5);

  const topLabels = topUsers.map((u) => u.email);
  const topData = topUsers.map((u) => u.used);

  const barData = {
    labels: topLabels,
    datasets: [
      {
        label: "MB",
        data: topData,
        backgroundColor: alpha(theme.palette.primary.main, 0.7),
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        borderRadius: 6,
        barThickness: "flex",
        maxBarThickness: 32,
      },
    ],
  };

  const barOptions = makeThemedOptions(theme, {
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.parsed.x ?? ctx.parsed} MB`,
        },
      },
    },
    scales: {
      x: { beginAtZero: true },
    },
  });

  // Last 5 registered users (email + date)
  const lastUsers = users
    .filter((u) => u?.created && dayjs(u.created).isValid())
    .sort((a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf())
    .slice(0, 5);

  // Active users (last month): build a cumulative chart like total, but only for users with recent lastLogin
  const activeSince = dayjs().subtract(1, "month");
  const activeUsers = users.filter(
    (u) => u?.lastLogin && dayjs(u.lastLogin).isValid() && dayjs(u.lastLogin).isAfter(activeSince)
  );

  // Top 5 inactive users: never logged in or lastLogin older than a month, oldest first
  const inactiveUsersTop5 = users
    .map((u) => {
      const hasValid = u?.lastLogin && dayjs(u.lastLogin).isValid();
      const ts = hasValid ? dayjs(u.lastLogin).valueOf() : -Infinity;
      return { id: u.id, email: u.email, lastLogin: u.lastLogin, ts };
    })
    .filter((u) => u.ts === -Infinity || dayjs(u.lastLogin).isBefore(activeSince))
    .sort((a, b) => a.ts - b.ts)
    .slice(0, 5);

  const activeValidDates = activeUsers
    .map((u) => u?.created)
    .filter(Boolean)
    .map((d) => dayjs(d))
    .filter((d) => d.isValid())
    .map((d) => d.startOf("month"));

  let activeLabels;
  let activeDataPoints;
  if (activeValidDates.length > 0) {
    const aStart = activeValidDates.reduce((min, d) => (d.isBefore(min) ? d : min), activeValidDates[0]);
    const aEnd = dayjs().startOf("month");
    const aCounts = new Map();
    activeValidDates.forEach((d) => {
      const key = d.format("YYYY-MM");
      aCounts.set(key, (aCounts.get(key) || 0) + 1);
    });
    const aMonths = [];
    let cursorA = aStart.clone();
    while (cursorA.isBefore(aEnd) || cursorA.isSame(aEnd)) {
      aMonths.push(cursorA.format("YYYY-MM"));
      cursorA = cursorA.add(1, "month");
    }
    activeLabels = aMonths;
    let runningA = 0;
    activeDataPoints = aMonths.map((m) => {
      runningA += aCounts.get(m) || 0;
      return runningA;
    });
  } else {
    activeLabels = ["Активні користувачі"];
    activeDataPoints = [activeUsers.length || 0];
  }

  const activeLineDataset = {
    labels: activeLabels,
    datasets: [
      {
        label: "Активні (ост. місяць) — накопичено",
        data: activeDataPoints,
        borderColor: theme.palette.success?.main || primary,
        backgroundColor: alpha(theme.palette.success?.main || primary, 0.25),
        fill: true,
        tension: 0.3,
        pointRadius: 2.5,
      },
    ],
  };

  return (
    <MDBContainer fluid className="mt-4">
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <Grid container spacing={2}>
        {/* Total users */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1">Кількість користувачів</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Всього: {users.length}
              </Typography>
            </Box>
            <Box sx={{ width: "100%", aspectRatio: "1 / 1" }}>
              {loadingData ? (
                <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box color="error.main" p={2} sx={{ height: "100%" }}>
                  {error}
                </Box>
              ) : (
                <Line style={{ width: "100%", height: "100%" }} data={lineDataset} options={lineOptions} />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Subscription distribution */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1">Розподіл підписок</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Користувачів: {subTotal}
              </Typography>
            </Box>
            <Box sx={{ width: "100%", aspectRatio: "1 / 1" }}>
              {loadingData ? (
                <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box color="error.main" p={2} sx={{ height: "100%" }}>
                  {error}
                </Box>
              ) : subTotal === 0 ? (
                <Box p={2} color="text.secondary" sx={{ height: "100%" }}>Немає даних для відображення</Box>
              ) : (
                <Doughnut style={{ width: "100%", height: "100%" }} data={doughnutData} options={doughnutOptions} />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Top 5 by used storage */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1">ТОП-5 за використаним місцем</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Одиниці: MB
              </Typography>
            </Box>
            <Box sx={{ width: "100%", aspectRatio: "1 / 1" }}>
              {loadingData ? (
                <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
                  <CircularProgress />
                </Box>
              ) : usageError ? (
                <Box color="error.main" p={2} sx={{ height: "100%" }}>
                  {usageError}
                </Box>
              ) : topUsers.length === 0 ? (
                <Box p={2} color="text.secondary" sx={{ height: "100%" }}>Немає даних для відображення</Box>
              ) : (
                <Bar style={{ width: "100%", height: "100%" }} data={barData} options={barOptions} />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Last 5 registered */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1">Останні зареєстровані користувачі</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Останні 5
              </Typography>
            </Box>
            {loadingData ? (
              <Box sx={{ display: "grid", placeItems: "center", height: 120 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box color="error.main" p={2}>
                {error}
              </Box>
            ) : lastUsers.length === 0 ? (
              <Box p={2} color="text.secondary">Немає даних для відображення</Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Створено</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lastUsers.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{dayjs(u.created).format("YYYY-MM-DD HH:mm")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>

        {/* Active users (last month) */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1">Активні користувачі (ост. місяць)</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Всього активних: {activeUsers.length}
              </Typography>
            </Box>
            <Box sx={{ width: "100%", aspectRatio: "1 / 1" }}>
              {loadingData ? (
                <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box color="error.main" p={2} sx={{ height: "100%" }}>
                  {error}
                </Box>
              ) : activeUsers.length === 0 ? (
                <Box p={2} color="text.secondary" sx={{ height: "100%" }}>Немає активних користувачів за останній місяць</Box>
              ) : (
                <Line style={{ width: "100%", height: "100%" }} data={activeLineDataset} options={lineOptions} />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Top 5 inactive users */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1">ТОП-5 неактивних користувачів</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                За датою останнього логіну
              </Typography>
            </Box>
            {loadingData ? (
              <Box sx={{ display: "grid", placeItems: "center", height: 120 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box color="error.main" p={2}>
                {error}
              </Box>
            ) : inactiveUsersTop5.length === 0 ? (
              <Box p={2} color="text.secondary">Немає даних для відображення</Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Останній логін</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inactiveUsersTop5.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.ts === -Infinity ? "—" : dayjs(u.lastLogin).format("YYYY-MM-DD HH:mm")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>
    </MDBContainer>
  );
};

export default DashboardPage;
